import {
  KpiCategories,
  KpiKeys,
  KpiNames,
  KpiTableRow,
  KpiTypes,
  MilestoneName,
  StageName
} from '@models';
import { featureFlags } from 'environments/environment';

const STUDY_KPI_KEYS_TO_NAMES_MAP: Partial<Record<KpiKeys, KpiNames>> = {
  active: 'Site activation to contract',
  enrollment: 'Non-enrolling sites',
  earlyterm: 'Subject early termination rate',
  enrolled: 'Subjects enrolled to contract',
  subject_screen_fail_rate: 'Subject screen fail rate',
  deviation_rate_per_subject: 'Deviation rate per subject',
  days_to_close_site_findings: 'Days to close site findings',
  crf_review_completed: 'CRF review completed',
  clinical_material_kits_shipped: 'Clinical material kits shipped',
  on_time_dispatch_clinical_materials: 'On-time dispatch of clinical materials',
  key_milestones_on_track_to_baseline: 'Key milestones on track to baseline',
  days_to_pay_sites: 'Days to pay sites',
  lab_sample_query_rate: 'Lab sample query rate per visit',
  ereq_usage_for_lab_samples: 'eReq usage for lab samples',
  lab_kits_shipped: 'Lab kits shipped',
  lab_kit_wastage: 'Lab kit wastage',
  source_data_verification: 'Source data verification compliance',
  critical_or_major_quality_events: 'Critical or major quality events',
  spend_to_budget: 'Spend to budget'
};

const STUDY_KPI_CATEGORIES_TO_KEYS_MAPPING: Partial<Record<KpiCategories, KpiKeys[]>> = {
  'Budget & resource utilization': [
    'clinical_material_kits_shipped',
    'days_to_pay_sites',
    'lab_kit_wastage',
    'spend_to_budget'
  ],
  'Data quality & compliance': [
    'crf_review_completed',
    'deviation_rate_per_subject',
    'days_to_close_site_findings',
    'lab_sample_query_rate',
    'ereq_usage_for_lab_samples',
    'source_data_verification'
  ],
  'Recruitment & retention': ['enrollment', 'earlyterm', 'enrolled', 'subject_screen_fail_rate'],
  'Timeline & milestone adherence': [
    'on_time_dispatch_clinical_materials',
    'key_milestones_on_track_to_baseline',
    'active',
    'lab_kits_shipped'
  ],
  'Risk & issue management': ['critical_or_major_quality_events']
};

const STUDY_KPI_KEYS_TO_TYPE_MAPPING: Partial<Record<KpiKeys, KpiTypes>> = {
  active: 'percent',
  enrollment: 'percent',
  earlyterm: 'percent',
  enrolled: 'percent',
  subject_screen_fail_rate: 'percent',
  deviation_rate_per_subject: 'number',
  days_to_close_site_findings: 'days',
  crf_review_completed: 'percent',
  clinical_material_kits_shipped: 'number',
  on_time_dispatch_clinical_materials: 'percent',
  key_milestones_on_track_to_baseline: 'percent',
  days_to_pay_sites: 'number',
  source_data_verification: 'percent',
  lab_sample_query_rate: 'percent',
  ereq_usage_for_lab_samples: 'percent',
  lab_kits_shipped: 'number',
  lab_kit_wastage: 'percent',
  critical_or_major_quality_events: 'number',
  spend_to_budget: 'percent'
};

const STUDY_KPI_KEYS_TO_STAGES_MAPPING: Partial<Record<KpiKeys, StageName[]>> = {
  active: ['Startup', 'Enrollment'],
  enrollment: ['Enrollment'],
  enrolled: ['Enrollment'],
  subject_screen_fail_rate: ['Enrollment'],
  source_data_verification: ['Maintenance'],
  deviation_rate_per_subject: ['Maintenance'],
  earlyterm: ['Enrollment', 'Maintenance'],
  days_to_close_site_findings: ['Enrollment', 'Maintenance'],
  clinical_material_kits_shipped: ['Startup', 'Enrollment', 'Maintenance'],
  on_time_dispatch_clinical_materials: ['Startup', 'Enrollment', 'Maintenance'],
  crf_review_completed: ['Enrollment', 'Maintenance', 'Closeout'],
  days_to_pay_sites: ['Enrollment', 'Maintenance', 'Closeout'],
  key_milestones_on_track_to_baseline: ['Startup', 'Enrollment', 'Maintenance', 'Closeout'],
  lab_sample_query_rate: ['Enrollment'],
  ereq_usage_for_lab_samples: ['Enrollment'],
  lab_kits_shipped: ['Enrollment'],
  lab_kit_wastage: ['Enrollment'],
  critical_or_major_quality_events: ['Startup', 'Enrollment', 'Maintenance', 'Closeout'],
  spend_to_budget: ['Startup', 'Enrollment', 'Maintenance', 'Closeout']
};

const STUDY_KPI_TOOLTIP_CONTENTS_MAP: Partial<
  Record<KpiKeys, { description: string; keyMilestones?: MilestoneName[]; thresholds?: string[] }>
> = {
  earlyterm: {
    description:
      'Percent of enrolled subjects that withdraw early from a study relative to total enrolled subjects',
    thresholds: ['≤ 20% = On track', '> 20% = Off track']
  },
  enrolled: {
    description: 'Percent of actual subjects enrolled relative to target through current date',
    thresholds: ['≥ 85% = On track', '≥ 80% and < 85% = At risk', '< 80% = Off track']
  },
  enrollment: {
    description:
      'Percent of sites that have been active for at least 60 days with no subjects enrolled',
    thresholds: ['<23% = On track', '≥23% and <25% = At risk', '≥25% = Off track']
  },
  active: {
    description:
      'Percent of activated sites relative to the expected number of activated sites for the current date',
    thresholds: ['≥85% = On track', '≥70% and <85% = At risk', '<70% = Off track']
  },
  subject_screen_fail_rate: {
    description: 'Percent of screened subjects that do not enroll in a study'
  },
  deviation_rate_per_subject: {
    description: 'Average number of protocol deviations per screened subject',
    thresholds: ['≤0.5 = On track', '>0.5 and ≤1 = At risk', '>1 = Off track']
  },
  days_to_close_site_findings: {
    description: 'Average number of days to close a site finding at active sites',
    thresholds: ['<45 days = On track', '≥45 and ≤50 days = At risk', '>50 days = Off track']
  },
  crf_review_completed: {
    description: 'Percent of CRA site visits that have completed a Case Report Form (CRF) review',
    thresholds: ['≥80% = On track', '≥70% and <80% = At risk', '<70% = Off track']
  },
  clinical_material_kits_shipped: {
    description:
      'Number of kits shipped of investigated project and clinical ancillary materials from Thermo Fisher facility'
  },
  on_time_dispatch_clinical_materials: {
    description:
      'Percent of shipments of investigated project and clinical ancillary materials that are dispatched from Thermo Fisher facility on time',
    thresholds: ['≥99% = On track', '<99% = Off track']
  },
  days_to_pay_sites: {
    description: 'Average number of days for payment to study sites'
  },
  key_milestones_on_track_to_baseline: {
    description:
      'Percent of key milestones with actual completed date on or before the baseline target date.  Milestones included in this calculation are',
    // for this particular KPI we don't use the list in study-details only in portfolio, instead we use milestones based on isKeyMilestone flag in Milestone model
    keyMilestones: [
      'First study site activated',
      'Last site activated',
      'First subject enrolled',
      'Last subject enrolled',
      'Last subject last visit',
      'Database lock',
      'Tables, listings, and figures available',
      'Final clinical study report delivery'
    ],
    thresholds: ['>80% = On track', '≥70% and ≤80% = At risk', '<70% = Off track']
  },
  source_data_verification: {
    description: 'Percent of subject visits that have a completed Source Data Verification (SDV)',
    keyMilestones: ['Database lock'],
    thresholds: ['>80% = On track', '≥70% and ≤80% = At risk', '<70% = Off track']
  },
  lab_sample_query_rate: {
    description:
      'Total number of queries for lab samples relative to the total number of subject visits for which a sample is received.',
    thresholds: ['≥0 and ≤0.5 = On track', '>0.5 = Off track']
  },
  ereq_usage_for_lab_samples: {
    description:
      "Percent of subject lab samples that are returned for analysis using PPD's eRequisition (eReq) system.",
    thresholds: ['>80% = On track', '≤80% = Off track']
  },
  lab_kits_shipped: {
    description: 'Number of lab kits shipped to investigator sites from PPD labs'
  },
  lab_kit_wastage: {
    description:
      'Percentage of lab kits that were shipped to investigator sites and expired prior to use.',
    thresholds: ['≥0% and ≤40% = On track', '>40% = Off track']
  },
  critical_or_major_quality_events: {
    description:
      'Number of quality events that are classified as either Critical or Major following triage.'
  },
  spend_to_budget: {
    description: 'Direct spend relative to contracted budget'
  }
};

const STUDY_X_OF_Y_SECONDARY_VALUES_KPIS: KpiKeys[] = [
  'active',
  'enrollment',
  'earlyterm',
  'enrolled',
  'subject_screen_fail_rate',
  'crf_review_completed',
  'on_time_dispatch_clinical_materials',
  'deviation_rate_per_subject',
  'source_data_verification',
  'key_milestones_on_track_to_baseline',
  'lab_sample_query_rate',
  'ereq_usage_for_lab_samples',
  'lab_kit_wastage',
  'spend_to_budget'
];

const STUDY_X_SECONDARY_VALUE_KPIS: KpiKeys[] = ['critical_or_major_quality_events'];

const STUDY_KPI_TABLE_LABEL_MAP: Partial<Record<keyof KpiTableRow, string>> = {
  name: 'KPI Name',
  value: 'Value',
  trend: 'Trend',
  status: 'Status'
};

const DISABLED_KPI_CLICKS: KpiKeys[] = ['critical_or_major_quality_events', 'spend_to_budget'];

// Helper function to filter out hidden KPIs from catalogue objects
function filterHiddenKpis<T extends Record<string, any>>(catalogue: T): T {
  const filtered = { ...catalogue };
  featureFlags.hiddenKPIs.forEach((hiddenKey) => {
    delete filtered[hiddenKey as KpiKeys];
  });
  return filtered;
}

// Helper function to filter hidden KPIs from category mappings
function filterCategoryMappings(
  mapping: Partial<Record<KpiCategories, KpiKeys[]>>
): Partial<Record<KpiCategories, KpiKeys[]>> {
  const filtered = { ...mapping };
  Object.keys(filtered).forEach((category) => {
    const cat = category as KpiCategories;
    if (filtered[cat]) {
      filtered[cat] = filtered[cat]!.filter((key) => !featureFlags.hiddenKPIs.includes(key));
    }
  });
  return filtered;
}

// Apply filtering to exported catalogues
const FILTERED_STUDY_KPI_KEYS_TO_NAMES_MAP = filterHiddenKpis(STUDY_KPI_KEYS_TO_NAMES_MAP);
const FILTERED_STUDY_KPI_CATEGORIES_TO_KEYS_MAPPING = filterCategoryMappings(
  STUDY_KPI_CATEGORIES_TO_KEYS_MAPPING
);
const FILTERED_STUDY_KPI_KEYS_TO_TYPE_MAPPING = filterHiddenKpis(STUDY_KPI_KEYS_TO_TYPE_MAPPING);
const FILTERED_STUDY_KPI_KEYS_TO_STAGES_MAPPING = filterHiddenKpis(
  STUDY_KPI_KEYS_TO_STAGES_MAPPING
);
const FILTERED_STUDY_KPI_TOOLTIP_CONTENTS_MAP = filterHiddenKpis(STUDY_KPI_TOOLTIP_CONTENTS_MAP);
const FILTERED_STUDY_X_OF_Y_SECONDARY_VALUES_KPIS = STUDY_X_OF_Y_SECONDARY_VALUES_KPIS.filter(
  (key) => !featureFlags.hiddenKPIs.includes(key)
);
const FILTERED_STUDY_X_SECONDARY_VALUE_KPIS = STUDY_X_SECONDARY_VALUE_KPIS.filter(
  (key) => !featureFlags.hiddenKPIs.includes(key)
);

export {
  DISABLED_KPI_CLICKS,
  FILTERED_STUDY_KPI_CATEGORIES_TO_KEYS_MAPPING as STUDY_KPI_CATEGORIES_TO_KEYS_MAPPING,
  FILTERED_STUDY_KPI_KEYS_TO_NAMES_MAP as STUDY_KPI_KEYS_TO_NAMES_MAP,
  FILTERED_STUDY_KPI_KEYS_TO_STAGES_MAPPING as STUDY_KPI_KEYS_TO_STAGES_MAPPING,
  FILTERED_STUDY_KPI_KEYS_TO_TYPE_MAPPING as STUDY_KPI_KEYS_TO_TYPE_MAPPING,
  STUDY_KPI_TABLE_LABEL_MAP,
  FILTERED_STUDY_KPI_TOOLTIP_CONTENTS_MAP as STUDY_KPI_TOOLTIP_CONTENTS_MAP,
  FILTERED_STUDY_X_OF_Y_SECONDARY_VALUES_KPIS as STUDY_X_OF_Y_SECONDARY_VALUES_KPIS,
  FILTERED_STUDY_X_SECONDARY_VALUE_KPIS as STUDY_X_SECONDARY_VALUE_KPIS
};
